import { collect } from 'collect.js';
import moment from 'moment-timezone';
import DateHelpers from './DateHelpers';

class WorkScheduleHelpers {
  formatTimeUnit = (unit) => (unit < 10 ? `0${unit}` : unit);

  formatHoursAndMinutes = (hour, minute) =>
    hour !== null && minute !== null ? `${this.formatTimeUnit(hour)}:${this.formatTimeUnit(minute)}` : null;

  scheduleUnitToString = (unit) =>
    `${this.formatHoursAndMinutes(unit.open.hour, unit.open.minute)}-${this.formatHoursAndMinutes(unit.close.hour, unit.close.minute)}`;

  parseHour = (time) => parseInt(time.slice(0, 2), 10);

  parseMinute = (time) => parseInt(time.slice(-2), 10);

  isCompleteTime = (time) => typeof time === 'string' && /^\d{2}:\d{2}$/.test(time);

  /**
   * Whether a days + open/close entry can be turned into schedule units. The time
   * inputs start empty and are typed digit by digit, so a save before both are full
   * HH:mm would hand createDayBreakdown a null or partial time.
   */
  isCompleteEntry = (days, openTime, closeTime) =>
    days?.length > 0 && this.isCompleteTime(openTime) && this.isCompleteTime(closeTime);

  getDaysLabel = (items) => {
    if (items?.length === 7) {
      return 'Every day';
    }

    let lastItem = null;

    return collect(items)
      .reduce((carry, item) => {
        if (!lastItem || item.open.day - lastItem.open.day !== 1) {
          lastItem = item;

          return carry.push(collect([item]));
        }

        lastItem = item;
        const current = carry.pop();

        return carry.push(collect([...current, item]));
      }, collect())
      .map((chunk) =>
        chunk.count() > 1
          ? `${DateHelpers.getDayName(chunk.first().open.day)}-${DateHelpers.getDayName(chunk.last().open.day)}`
          : DateHelpers.getDayName(chunk.first().open.day),
      )
      .implode(', ');
  };

  groupSchedule = (schedule) =>
    collect(schedule)
      .mapToGroups((item) => [this.scheduleUnitToString(item), item])
      .map((items, time) => {
        const item = collect(items).first();

        return {
          open_hour: item.open.hour,
          open_minute: item.open.minute,
          close_hour: item.close.hour,
          close_minute: item.close.minute,
          units: items,
          time_label: time,
          days_label: this.getDaysLabel(items),
        };
      })
      .values();

  createDayBreakdown = (day, openTime, closeTime) => {
    const resolveCloseDay = (openHour, openMinute, closeHour, closeMinute) =>
      moment({ hour: openHour, minute: openMinute }).isBefore(moment({ hour: closeHour, minute: closeMinute }))
        ? day
        : day + 1;

    const openHour = this.parseHour(openTime);
    const openMinute = this.parseMinute(openTime);
    const closeHour = this.parseHour(closeTime);
    const closeMinute = this.parseMinute(closeTime);
    const closeDay = resolveCloseDay(openHour, openMinute, closeHour, closeMinute);

    return {
      open: {
        day,
        hour: openHour,
        minute: openMinute,
      },
      close: {
        day: closeDay === 7 ? 0 : closeDay,
        hour: closeHour,
        minute: closeMinute,
      },
    };
  };
}

export default new WorkScheduleHelpers();
